import { DollarSign, FileText, Building, Briefcase } from 'lucide-react';

const tabConfig = {
    'active-work': {
        title: 'Active Work',
        icon: Briefcase,
        countKey: 'pending_jobs_count',
        itemsKey: 'pending_jobs',
        emptyMessage: 'No active jobs at the moment',
    },
    'requests': {
        title: 'Requests',
        icon: FileText,
        countKey: null,
        itemsKey: null,
        emptyMessage: 'No requests yet',
    },
    'quotes': {
        title: 'Accepted Quotes',
        icon: DollarSign,
        countKey: 'accepted_quotes_count',
        itemsKey: 'accepted_quotes',
        emptyMessage: 'No accepted quotes',
    },
    'jobs': {
        title: 'All Jobs',
        icon: Building,
        countKey: 'all_jobs_count',
        itemsKey: 'all_jobs',
        emptyMessage: 'No jobs scheduled',
    },
    'invoices': {
        title: 'Invoices',
        icon: DollarSign,
        countKey: 'invoices_count',
        itemsKey: 'invoices',
        emptyMessage: 'No invoices issued',
        totalAmountKey: 'total_invoices_amount',
    },
};

export default function ClientOverviewTabs({ activeTab, overview }) {
    const config = tabConfig[activeTab];

    if (!config) {
        return <div className="p-12 text-center text-gray-500">Tab not found</div>;
    }

    const count = config.countKey ? overview[config.countKey] || 0 : null;
    const items = config.itemsKey ? overview[config.itemsKey] || [] : [];
    const hasData = items.length > 0;

    const Icon = config.icon;

    if (!hasData) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="text-gray-400 mb-4">
                    <Icon className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-gray-500 font-medium">{config.emptyMessage}</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">
                    {config.title} ({count !== null ? count : items.length})
                </h3>
            </div>

            <div className="divide-y divide-gray-100">
                {items.map((item, i) => (
                    <div key={i} className="p-5 hover:bg-gray-50 transition">
                        {activeTab === 'active-work' && (
                            <div className="w-full">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <div className="font-bold text-gray-900 text-lg">{item.job_number}</div>
                                        <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                                            <span className="flex items-center gap-1">
                                                <Building className="w-3 h-3" />
                                                {new Date(item.start_date).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric'
                                                })}
                                            </span>
                                            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                            <span className="text-emerald-600 font-medium">{item.quote?.service}</span>
                                        </div>
                                    </div>
                                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full capitalize ${item.status === 'pending' ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-gray-100 text-gray-800'
                                        }`}>
                                        {item.status}
                                    </span>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-3">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-gray-500">Boat</span>
                                        <span className="font-semibold text-gray-900 text-right truncate max-w-[200px]">{item.quote?.boat}</span>
                                    </div>

                                    {item.assigned_surveyors?.length > 0 && (
                                        <div className="pt-3 border-t border-gray-200">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Assigned Surveyors</span>
                                                <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{item.assigned_surveyors.length}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {item.assigned_surveyors.map((surveyor) => (
                                                    <div key={surveyor.id} className="flex items-center gap-2 bg-white px-2.5 py-1.5 rounded-lg border border-gray-200 shadow-sm hover:border-emerald-200 transition-colors">
                                                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                                                            {surveyor.name.charAt(0)}
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="text-xs font-bold text-gray-800 leading-none">{surveyor.name}</span>
                                                            <span className="text-[10px] text-gray-500 leading-tight mt-0.5">{surveyor.phone}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'quotes' && (
                            <div className="space-y-2 text-sm">
                                <div className="font-medium text-gray-900">{item.service}</div>
                                <div className="text-gray-600">{item.boat} • {item.boat_type}</div>
                                <div className="text-gray-500 text-xs">{item.boat_location}</div>
                                <div className="font-bold text-emerald-600">${item.accepted_offer_price?.toLocaleString()}</div>
                            </div>
                        )}

                        {activeTab === 'invoices' && (
                            <div className="space-y-3 w-full">
                                <div className="flex justify-between items-start border-b border-gray-100 pb-2">
                                    <div>
                                        <div className="font-bold text-gray-900">Invoice #{item.invoice_id}</div>
                                        <div className="text-xs text-gray-500">
                                            {new Date(item.created_at).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            })}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-bold text-emerald-600 text-lg">${item.total?.toLocaleString()}</div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                                    {item.services?.map((service, idx) => (
                                        <div key={idx} className="flex justify-between text-xs group">
                                            <span className="text-gray-600 group-hover:text-gray-900 transition-colors">{service.name}</span>
                                            <span className="font-medium text-gray-900">
                                                ${Number(service.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab === 'jobs' && (
                            <div className="text-sm text-gray-600">
                                No job details available yet
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {activeTab === 'invoices' && config.totalAmountKey && (
                <div className="p-5 bg-emerald-50 border-t border-emerald-200">
                    <div className="text-center">
                        <p className="text-sm text-gray-600">Total Invoiced Amount</p>
                        <p className="text-2xl font-bold text-emerald-700">
                            ${overview[config.totalAmountKey]?.toLocaleString()}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}